import type { CSSProperties, ReactNode } from 'react';
import { ChevronDown, EyeOff, type LucideIcon } from 'lucide-react';
import { useDashboardPrefs } from './dashboardPrefs';
import styles from './DashboardSections.module.css';

/** A colour from the theme's highlight set: each card keeps its own, in both themes. */
export type Tone = 'red' | 'amber' | 'green' | 'cyan' | 'blue' | 'violet' | 'magenta';
export const toneStyle = (tone: Tone) => ({ '--tone': `var(--hl-${tone})` }) as CSSProperties;

/**
 * One card. `size` sets its share of a row: `wide` takes twice a plain card's
 * share and `full` the whole row; whatever the others leave, it grows into.
 * On the Dashboard a card can be collapsed to its header or hidden, as the
 * person chooses (`dashboardPrefs`); elsewhere it has neither control.
 * `emphasis` lifts the one card the page leads with (Needs you).
 */
export function Card({ title, testId, icon: Icon, tone, size = 'plain', emphasis = false, children, more }: {
  title: string; testId: string; icon: LucideIcon; tone: Tone; size?: 'plain' | 'wide' | 'full'; emphasis?: boolean; children: ReactNode; more?: ReactNode;
}) {
  const prefs = useDashboardPrefs();
  if (prefs?.hidden.has(testId)) return null;
  const collapsed = prefs?.collapsed.has(testId) ?? false;
  return (
    <section className={styles.card} data-card data-size={size} data-emphasis={emphasis || undefined} data-collapsed={collapsed || undefined} data-testid={testId} aria-label={title} style={toneStyle(tone)}>
      <header className={styles.cardHead}>
        <span className={styles.badge} aria-hidden="true"><Icon size={15} /></span>
        <h3 className={styles.cardTitle}>{title}</h3>
        {!collapsed && more}
        {prefs && (
          <span className={styles.cardControls}>
            <button type="button" className={styles.cardControl} aria-expanded={!collapsed}
              aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`} title={collapsed ? 'Expand' : 'Collapse'}
              onClick={() => prefs.toggleCollapsed(testId)}>
              <ChevronDown size={14} aria-hidden="true" className={styles.chevron} />
            </button>
            <button type="button" className={styles.cardControl} aria-label={`Hide ${title}`} title="Hide this card"
              onClick={() => prefs.hide(testId)}>
              <EyeOff size={14} aria-hidden="true" />
            </button>
          </span>
        )}
      </header>
      {!collapsed && children}
    </section>
  );
}
